import React from 'react';
import { Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';
import { surfaceBackgroundSx } from '../../theme/backgrounds';
import { sectionContainerSx, sectionStackGap } from '../../theme/standards';

interface PlannerLayoutProps {
  overview: React.ReactNode;
  destinationInsights?: React.ReactNode;
  mainContent: React.ReactNode;
  weatherPanel?: React.ReactNode;
  sidePanel?: React.ReactNode;
  footer?: React.ReactNode;
  sectionSx?: SystemStyleObject<Theme>;
  footerSx?: SystemStyleObject<Theme>;
  mainContentSx?: SystemStyleObject<Theme>;
  transparentBackground?: boolean;
}

export const PlannerLayout: React.FC<PlannerLayoutProps> = ({
  overview,
  destinationInsights,
  mainContent,
  weatherPanel,
  sidePanel,
  footer,
  sectionSx,
  footerSx,
  mainContentSx,
  transparentBackground,
}) => {
  return (
    <Box
      sx={(theme) => ({
        minHeight: '100vh',
        overflow: 'visible',
        position: 'relative',
        ...(transparentBackground ? { backgroundColor: 'transparent' } : surfaceBackgroundSx(theme)),
      })}
    >
      <Box
        component="section"
        sx={{
          ...(sectionContainerSx as SystemStyleObject<Theme>),
          ...(sectionSx || {}),
          overflow: 'visible',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: sectionStackGap, overflow: 'visible' }}>
          {/* Main grid with overview, main content, weather, and maps */}
          <Box
            sx={{
              display: 'grid',
              gap: { xs: 4, lg: 6 },
              gridTemplateColumns: {
                xs: '1fr',
                md: (weatherPanel || sidePanel) ? 'repeat(12, 1fr)' : '1fr',
              },
              alignItems: { xs: 'start', lg: 'start' },
              overflow: 'visible',
            }}
          >
            {/* Left column: Overview and main content */}
            <Box
              sx={{
                gridColumn: {
                  xs: 'span 12',
                  md: (weatherPanel || sidePanel) ? 'span 8' : 'span 12',
                  lg: (weatherPanel || sidePanel) ? 'span 7' : 'span 12',
                },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Overview - always first */}
              {overview}

              {/* Mobile: Show destination insights after overview */}
              {destinationInsights && (
                <Box sx={{
                  mt: { xs: 4, md: 6 },
                  display: { xs: 'block', md: 'none' },
                }}>
                  {destinationInsights}
                </Box>
              )}

              {/* Mobile: Show map after destination insights */}
              {sidePanel && (
                <Box sx={{
                  mt: 4,
                  display: { xs: 'block', md: 'none' },
                }}>
                  {sidePanel}
                </Box>
              )}

              {/* Mobile: Show weather after map */}
              {weatherPanel && (
                <Box sx={{
                  mt: 4,
                  display: { xs: 'block', md: 'none' },
                }}>
                  {weatherPanel}
                </Box>
              )}

              {/* Desktop: Show destination insights before main content */}
              {destinationInsights && (
                <Box sx={{
                  mt: { xs: 4, md: 6 },
                  display: { xs: 'none', md: 'block' },
                }}>
                  {destinationInsights}
                </Box>
              )}

              {/* Main content section */}
              <Box sx={{ mt: { xs: 4, md: 6 }, ...(mainContentSx || {}) }}>
                {mainContent}
              </Box>
            </Box>

            {/* Right column: Weather and maps - Desktop only */}
            {(weatherPanel || sidePanel) && (
              <Box
                sx={{
                  gridColumn: {
                    xs: 'span 12',
                    md: 'span 4',
                    lg: 'span 5',
                  },
                  display: { xs: 'none', md: 'flex' },
                  flexDirection: 'column',
                  gap: 3,
                  alignSelf: 'stretch',
                  position: 'relative',
                }}
              >
                {weatherPanel && (
                  <Box sx={{ width: '100%' }}>
                    {weatherPanel}
                  </Box>
                )}
                {sidePanel && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      position: { xs: 'relative', md: 'sticky' },
                      top: { xs: 'auto', md: '24px' },
                      alignSelf: 'flex-start',
                      width: '100%',
                    }}
                  >
                    {sidePanel}
                  </Box>
                )}
              </Box>
            )}
          </Box>

        </Box>
      </Box>

      {footer && (
        <Box
          component="section"
          sx={{
            width: '100%',
            mt: 0,
            px: { xs: 1.5, sm: 2, md: 2.5, lg: 3 },
            pb: { xs: 5, md: 7 },
            ...footerSx,
          }}
        >
          {footer}
        </Box>
      )}
    </Box>
  );
};
